import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { Play, Download, Eye } from 'lucide-react';

const Payroll = () => {
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');

  useEffect(() => {
    const currentDate = new Date();
    const monthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(monthStr);
    fetchPayrollRecords(monthStr);
  }, []);

  const fetchPayrollRecords = async (month) => {
    try {
      const response = await apiClient.get(`/api/payroll/records?month=${month}`);
      setPayrollRecords(response.data);
    } catch (error) {
      console.error('Error fetching payroll records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRunPayroll = async () => {
    if (!selectedMonth) {
      alert('Please select a month');
      return;
    }

    setProcessing(true);
    try {
      const [year, month] = selectedMonth.split('-');
      await apiClient.post('/api/payroll/run', {
        month,
        year: parseInt(year)
      });
      fetchPayrollRecords(selectedMonth);
      alert('Payroll processed successfully!');
    } catch (error) {
      console.error('Error processing payroll:', error);
      alert('Error processing payroll');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadPayslip = async (recordId) => {
    try {
      const response = await apiClient.get(`/api/payroll/payslip/${recordId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${recordId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading payslip:', error);
    }
  };

  const handleExportPayroll = async () => {
    if (!selectedMonth) {
      alert('Please select a month first');
      return;
    }

    try {
      const response = await apiClient.get(`/api/payroll/export?month=${selectedMonth}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${selectedMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting payroll:', error);
      alert('Error exporting payroll: ' + (error.response?.data?.detail || 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(210_15%_92%)]">Payroll Management</h1>
          <p className="text-[hsl(215_10%_70%)] mt-1">Process and manage employee payroll</p>
        </div>
        <button
          onClick={handleExportPayroll}
          disabled={payrollRecords.length === 0}
          data-testid="export-payroll-button"
          className="flex items-center gap-2 bg-[#10B981] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#059669] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
        >
          <Download size={20} />
          Export to Excel
        </button>
      </div>

      {/* Payroll Actions */}
      <div className="bg-[hsl(220_6%_8%)] rounded-xl border border-[hsl(215_10%_22%)] p-6 mb-6">
        <h3 className="text-lg font-semibold text-[hsl(210_15%_92%)] mb-4">Run Payroll</h3>
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-[hsl(210_15%_92%)] mb-2">Select Month</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                fetchPayrollRecords(e.target.value);
              }}
              data-testid="payroll-month-select"
              className="px-4 py-2 bg-[hsl(220_6%_12%)] border border-[hsl(215_10%_22%)] rounded-lg text-[hsl(210_15%_92%)] focus:border-[#00B8B8] focus:ring-2 focus:ring-[#00B8B8]/20"
            />
          </div>
          <div className="flex-1"></div>
          <button
            onClick={handleRunPayroll}
            disabled={processing}
            data-testid="run-payroll-button"
            className="flex items-center gap-2 bg-[#00B8B8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0ED2D2] disabled:opacity-50 transition-colors duration-150"
          >
            <Play size={20} />
            {processing ? 'Processing...' : 'Run Payroll'}
          </button>
        </div>
      </div>

      {/* Payroll Records Table */}
      <div className="table-container">
        <table className="w-full">
          <thead className="table-header">
            <tr>
              <th className="text-left p-4">Emp Code</th>
              <th className="text-left p-4">Employee Name</th>
              <th className="text-left p-4">Month</th>
              <th className="text-left p-4">Paid Leave</th>
              <th className="text-left p-4">Gross Salary</th>
              <th className="text-left p-4">OT</th>
              <th className="text-left p-4">Sunday</th>
              <th className="text-left p-4">Deductions</th>
              <th className="text-left p-4">Net Salary</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {payrollRecords.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center p-8 text-[hsl(215_10%_70%)]">
                  No payroll records found for selected month
                </td>
              </tr>
            ) : (
              payrollRecords.map((record) => (
                <tr key={record.id} className="border-t border-[hsl(215_10%_22%)] hover:bg-[hsl(215_8%_20%)/50] transition-colors duration-150">
                  <td className="p-4 font-mono font-medium text-[hsl(210_15%_92%)]">{record.emp_code}</td>
                  <td className="p-4 font-medium text-[hsl(210_15%_92%)]">{record.employee_name}</td>
                  <td className="p-4 text-[hsl(215_10%_70%)]">{record.month}</td>
                  <td className="p-4 font-mono" data-testid={`payroll-paid-leave-${record.emp_code}`}>
                    <span className="text-[#0EA5E9]">{Number(record.paid_leave_days || 0)}</span>
                    <span className="text-[hsl(215_10%_70%)] text-xs"> / {Number(record.paid_leave_balance || 0)} bal</span>
                  </td>
                  <td className="p-4 font-mono font-medium text-[hsl(210_15%_92%)]">₹{Number(record.gross_salary).toLocaleString()}</td>
                  <td className="p-4 font-mono text-[#10B981]">
                    {Number(record.overtime || 0) > 0 ? `₹${Number(record.overtime).toLocaleString()}` : '-'}
                  </td>
                  <td className="p-4 font-mono text-[#0EA5E9]">
                    {Number(record.sunday_working || 0) > 0 ? `₹${Number(record.sunday_working).toLocaleString()}` : '-'}
                  </td>
                  <td className="p-4 font-mono text-[#EF4444]">₹{Number(record.total_deductions).toLocaleString()}</td>
                  <td className="p-4 font-mono font-bold text-[#10B981]">₹{Number(record.net_salary).toLocaleString()}</td>
                  <td className="p-4">
                    <span className="status-badge status-approved capitalize">
                      {record.status}
                    </span>
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => handleDownloadPayslip(record.id)}
                      data-testid={`download-payslip-${record.emp_code}`}
                      className="flex items-center gap-2 px-4 py-2 bg-[hsl(215_8%_20%)] text-[hsl(210_15%_92%)] border border-[hsl(215_10%_22%)] rounded-lg hover:bg-[hsl(215_8%_24%)] hover:border-[#00B8B8]/50 text-sm transition-colors duration-150"
                    >
                      <Download size={16} />
                      Payslip
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
};

export default Payroll;